import { Link } from 'react-router-dom';
import HeroCarousel from './components/HeroCarousel';
import SectionHeading from '@/components/feature/SectionHeading';
import MovieCard, { formatViews } from '@/components/feature/MovieCard';
import { movies, movieCategories } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Home() {
  usePageMeta({
    title: '韩国伦理 - 专注韩国伦理影视剧情及推荐网站',
    description:
      '韩国伦理网站致力于提供丰富的韩国伦理影视内容、剧情介绍和相关资讯，帮助用户了解并选择优质韩伦理影视作品。覆盖传统伦理剧及现代伦理题材。',
    keywords: '韩国伦理,韩国伦理影视,韩国伦理剧,韩国伦理剧情,韩国电视剧,韩国电影,伦理影视推荐,韩剧伦理',
  });

  const latest = [...movies].sort((a, b) => b.year - a.year).slice(0, 6);
  const popular = [...movies].sort((a, b) => b.views - a.views).slice(0, 5);

  return (
    <div>
      <HeroCarousel />

      {/* Category quick entries */}
      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            title="分类快捷入口"
            desc="从题材出发，快速找到你想看的韩国伦理影视作品。"
            moreTo="/category"
            moreLabel="进入分类页"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {movieCategories.map((cat) => (
              <Link
                key={cat.key}
                to={`/category?cat=${cat.key}`}
                className="group relative h-[220px] md:h-[260px] rounded-lg overflow-hidden bg-primary-950 cursor-pointer"
              >
                <img
                  src={cat.image}
                  alt={cat.label}
                  title={`${cat.label} 韩国伦理影视分类`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700 opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                  <h3 className="font-heading text-lg md:text-xl font-bold text-background-50">{cat.label}</h3>
                  <p className="mt-2 text-sm text-background-200/85 leading-relaxed line-clamp-2">{cat.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm text-accent-400">
                    查看作品 <i className="ri-arrow-right-s-line" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest updates */}
      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            title="最新更新伦理影视"
            desc="按首映年份收录的最新韩国伦理影视作品，涵盖剧集与电影。"
            moreTo="/category?sort=newest"
            moreLabel="查看全部更新"
          />
          {latest.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {latest.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          ) : (
            <p className="py-16 text-center text-foreground-500">暂无更新内容</p>
          )}
        </div>
      </section>

      {/* Popular recommendations */}
      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeading
            title="热门推荐伦理影视"
            desc="基于访问量与编辑推荐整理的高热度韩国伦理影视作品。"
            moreTo="/category?sort=hot"
            moreLabel="更多热门"
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
            {popular.map((m, i) => (
              <Link
                key={m.id}
                to={`/detail/${m.id}`}
                className="group flex gap-4 p-4 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <span
                  className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-md font-heading font-bold text-lg ${
                    i < 3 ? 'bg-accent-500 text-background-50' : 'bg-background-200 text-foreground-600'
                  }`}
                >
                  {i + 1}
                </span>
                <div className="w-[64px] h-[90px] shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img
                    src={m.cover}
                    alt={`${m.title} 海报`}
                    title={`${m.title} 韩国伦理影视`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {m.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-foreground-500">
                    <span>{m.categoryLabel}</span>
                    <span>·</span>
                    <span>{m.genre}</span>
                    <span>·</span>
                    <span>{m.year}</span>
                  </div>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{m.summary}</p>
                  <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-fire-line text-accent-500" /> {formatViews(m.views)}
                    </span>
                    <span className="text-accent-600 font-medium">{m.rating.toFixed(1)} 分</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Site intro band */}
      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 items-start">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1 h-6 rounded-full bg-accent-500" />
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">关于韩国伦理</h2>
              </div>
              <p className="text-sm md:text-base text-foreground-600 leading-loose">
                韩国伦理是一个专注韩国伦理影视内容展示与推荐的影视资讯网站。我们收录传统伦理剧、现代伦理剧与伦理电影，
                为每一部作品整理清晰的剧情介绍、主创信息与分类标签，帮助对韩国伦理题材感兴趣的观众快速了解、筛选并浏览优质作品。
                无论你想找一部适合深夜细读的静默之作，还是想系统梳理某一类伦理题材，这里都能成为你的入口。
              </p>
              <Link
                to="/about"
                className="mt-6 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                了解更多关于我们 <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
            <div className="grid grid-cols-3 lg:grid-cols-1 gap-4">
              <div className="p-5 rounded-lg bg-background-50 border border-background-200">
                <div className="font-heading text-2xl font-bold text-primary-600">{movies.length}</div>
                <div className="mt-1 text-xs text-foreground-500">精选影视作品</div>
              </div>
              <div className="p-5 rounded-lg bg-background-50 border border-background-200">
                <div className="font-heading text-2xl font-bold text-accent-600">{movieCategories.length}</div>
                <div className="mt-1 text-xs text-foreground-500">核心伦理分类</div>
              </div>
              <div className="p-5 rounded-lg bg-background-50 border border-background-200">
                <div className="font-heading text-2xl font-bold text-secondary-600">100%</div>
                <div className="mt-1 text-xs text-foreground-500">剧情详细介绍</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}